/**
 * PhaseGovernanceSection — advisory governing-event controls for leadership visibility
 * (SPEC-GOVERNING-EVENTS §6).
 *
 * ── WHY THIS FILE EXISTS (Phase F1) ────────────────────────────────────────────────────────────
 * This component previously lived at the bottom of `Phase0Panel.tsx`, which Phase F1 deletes. It was
 * only ever there because that file grew to 875 lines; it renders on the project **Overview** tab and
 * has nothing to do with Phase 0 artifact editing. Moved out verbatim — no behavioural change — so
 * that deleting the artifact page does not take an unrelated Overview card with it.
 */

import { useState } from "react";
import {
  Check,
  CheckCircle2,
  Clock,
  ExternalLink,
  Loader2,
  Shield,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import {
  useGoverningEvents,
  useGateCriteriaStatus,
} from "@/hooks/useGoverningEvents";
import type { GateState, GateCriterion } from "@/hooks/useGoverningEvents";

const GATE_WRITE_ROLES = ["pm", "sa", "admin"];

/** Format an ISO timestamp for the locked "Marked by … · …" line. */
function formatGovernanceDateTime(value: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
}

/** Format a YYYY-MM-DD (or ISO) date for compact display. */
function formatEvidenceDate(value: unknown): string {
  if (typeof value !== "string" || !value) return "";
  const d = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
}

/** Per-gate human-entered date field (null = no date picker for that gate). */
const GATE_DATE_FIELD: Record<number, { key: string; label: string } | null> = {
  0: { key: "kickoff_date", label: "Kickoff date" },
  1: null,
  2: { key: "ipr_date", label: "IPR date" },
  3: { key: "demo_date", label: "Demo date" },
  4: { key: "signoff_date", label: "Sign-off date" },
};

/** The date value to show on an approved gate row, pulled from its captured metadata. */
function metadataDate(metadata: Record<string, unknown> | null): string {
  if (!metadata) return "";
  const field = ["kickoff_date", "ipr_date", "demo_date", "signoff_date"].find(
    (k) => typeof metadata[k] === "string" && metadata[k],
  );
  return field ? formatEvidenceDate(metadata[field]) : "";
}

/** Compact read-only criteria checklist for the current gate's phase. */
function GateCriteriaChecklist({
  criteria,
}: {
  criteria: GateCriterion[];
}): JSX.Element | null {
  if (criteria.length === 0) return null;
  return (
    <div className="mt-1 flex flex-col gap-1 rounded-md bg-muted/40 p-2">
      <span className="text-xs font-medium text-text-secondary">
        Exit criteria
      </span>
      {criteria.map((c) => {
        const viaCia = c.checked_by === "system:cia";
        return (
          <div
            key={c.criterion_id}
            className="flex items-start gap-1.5 text-xs"
          >
            {c.is_checked ? (
              <CheckCircle2
                className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success"
                aria-hidden="true"
              />
            ) : (
              <Clock
                className="mt-0.5 h-3.5 w-3.5 shrink-0 text-text-disabled"
                aria-hidden="true"
              />
            )}
            <span
              className={
                c.is_checked ? "text-text-primary" : "text-text-disabled"
              }
            >
              {c.criterion_text}
              {viaCia && (
                <span className="ml-1 text-text-secondary">
                  · via CIA analysis
                </span>
              )}
              {!c.is_checked && (
                <span className="ml-1 text-text-disabled">· Pending</span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * PhaseGovernanceSection — advisory governing-event controls for leadership visibility
 * (SPEC-GOVERNING-EVENTS §6). Rendered on the project Overview tab so leadership can see gate
 * state at a glance. This CR renders only Gate 0 "Kickoff Scheduled":
 *   - pm/sa/admin see an active button while unapproved
 *   - once approved it locks to "Marked by {name} · {date}" for every role (B-03)
 * Advisory only — never blocks a phase transition (B-05). Button only — no date/time picker (B-04).
 */
export function PhaseGovernanceSection({
  projectId,
}: {
  projectId: string;
}): JSX.Element {
  const { user } = useAuth();
  const canApprove = GATE_WRITE_ROLES.includes(user?.role ?? "");
  const { events, confirmEvent, isLoading, isError, refetch } =
    useGoverningEvents(projectId);
  const { criteriaForPhase } = useGateCriteriaStatus(projectId);
  // Inline evidence-confirmation panel state (not a modal): which gate is expanded + its date input.
  const [panelOpen, setPanelOpen] = useState(false);
  const [dateValue, setDateValue] = useState("");

  function shell(children: React.ReactNode): JSX.Element {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center gap-2 space-y-0">
          <h3 className="flex items-center gap-2 text-base font-semibold text-text-primary">
            <Shield className="h-4 w-4 text-primary" aria-hidden="true" />
            Phase Governance
          </h3>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return shell(
      <div role="status" aria-label="Loading governance state">
        <Skeleton className="h-10 w-full" />
      </div>,
    );
  }

  if (isError || events.length === 0) {
    return shell(
      <div className="flex flex-col items-start gap-3">
        <p className="text-sm text-error">Failed to load governance state.</p>
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>,
    );
  }

  const sorted = [...events].sort((a, b) => a.gate - b.gate);
  const blockerIndex = sorted.findIndex((g) => !g.approved);
  const doneGates =
    blockerIndex === -1 ? sorted : sorted.slice(0, blockerIndex);
  const blockerGate = blockerIndex === -1 ? undefined : sorted[blockerIndex];

  const dateField = blockerGate ? GATE_DATE_FIELD[blockerGate.gate] : null;
  const blockerCriteria = blockerGate
    ? criteriaForPhase(`Phase ${blockerGate.gate}`)
    : [];

  function submitBlocker(gate: GateState): void {
    const field = GATE_DATE_FIELD[gate.gate];
    const metadata: Record<string, unknown> = {};
    if (field && dateValue) metadata[field.key] = dateValue;
    confirmEvent.mutate({
      gate: gate.gate,
      event_type: gate.event_type,
      metadata,
    });
    setPanelOpen(false);
    setDateValue("");
  }

  return shell(
    <div className="flex flex-col divide-y divide-outlines">
      {/* Locked green rows for every already-approved gate */}
      {doneGates.map((g) => {
        const meetingName =
          typeof g.metadata?.confirmed_in_meeting === "string"
            ? (g.metadata.confirmed_in_meeting as string)
            : "";
        const evidenceDate = metadataDate(g.metadata);
        const avomaLink =
          typeof g.metadata?.avoma_link === "string"
            ? (g.metadata.avoma_link as string)
            : "";
        return (
          <div
            key={g.gate}
            className="flex items-center justify-between gap-3 py-2 first:pt-0"
          >
            <div className="flex min-w-0 flex-col">
              <span className="text-sm font-medium text-text-primary">
                Gate {g.gate} · {g.label}
              </span>
              <span className="mt-0.5 flex items-center gap-1 text-xs text-success">
                <Check className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                Marked by {g.approved_by_name ?? "a team member"} ·{" "}
                {formatGovernanceDateTime(g.approved_at)}
              </span>
              {(evidenceDate || meetingName || avomaLink) && (
                <span className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-text-secondary">
                  {evidenceDate && <span>{evidenceDate}</span>}
                  {meetingName && <span>· {meetingName}</span>}
                  {avomaLink && (
                    <a
                      href={avomaLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-0.5 text-primary hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" aria-hidden="true" />
                      Avoma
                    </a>
                  )}
                </span>
              )}
            </div>
            <Badge variant="success">Done</Badge>
          </div>
        );
      })}

      {/* The single current blocking gate */}
      {blockerGate && (
        <div className="flex flex-col gap-2 py-2 first:pt-0">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-text-primary">
              Gate {blockerGate.gate} · {blockerGate.label}
            </span>
            {canApprove ? (
              <Button
                variant={panelOpen ? "outline" : "success"}
                size="sm"
                onClick={() => setPanelOpen((open) => !open)}
                disabled={confirmEvent.isPending}
              >
                {confirmEvent.isPending ? (
                  <Loader2
                    className="h-4 w-4 animate-spin"
                    aria-hidden="true"
                  />
                ) : (
                  <Check className="h-4 w-4" aria-hidden="true" />
                )}
                {panelOpen ? "Cancel" : blockerGate.label}
              </Button>
            ) : (
              <Badge variant="neutral">Pending</Badge>
            )}
          </div>

          {/* Inline evidence-confirmation panel (not a modal) */}
          {canApprove && panelOpen && (
            <div className="flex flex-col gap-2 rounded-md border border-border bg-muted/40 p-3">
              {blockerGate.evidence_hint?.meeting_name ? (
                <span className="flex flex-wrap items-center gap-1 text-xs text-text-secondary">
                  Evidence: {blockerGate.evidence_hint.meeting_name}
                  {blockerGate.evidence_hint.meeting_date && (
                    <span>
                      ·{" "}
                      {formatEvidenceDate(
                        blockerGate.evidence_hint.meeting_date,
                      )}
                    </span>
                  )}
                  {blockerGate.evidence_hint.avoma_link && (
                    <a
                      href={blockerGate.evidence_hint.avoma_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-0.5 text-primary hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" aria-hidden="true" />
                      Avoma
                    </a>
                  )}
                </span>
              ) : (
                <span className="text-xs text-text-disabled">
                  No linked meeting evidence found — you can still confirm.
                </span>
              )}

              {dateField && (
                // A wrapping <label> would fold the picker's button text into its name and make the
                // whole label a click target for the popover, so associate by id instead. Only one
                // blocker panel is ever rendered, so a static id is unique.
                <div className="flex flex-col gap-1 text-xs text-text-secondary">
                  <label htmlFor="blocker-gate-date">{dateField.label}</label>
                  <div className="max-w-[12rem]">
                    <DatePicker
                      id="blocker-gate-date"
                      value={dateValue}
                      onChange={setDateValue}
                    />
                  </div>
                </div>
              )}

              <div>
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => submitBlocker(blockerGate)}
                  disabled={confirmEvent.isPending}
                >
                  {confirmEvent.isPending ? (
                    <Loader2
                      className="h-4 w-4 animate-spin"
                      aria-hidden="true"
                    />
                  ) : (
                    <Check className="h-4 w-4" aria-hidden="true" />
                  )}
                  Confirm
                </Button>
              </div>
            </div>
          )}

          {/* Read-only exit-criteria checklist for the current gate */}
          <GateCriteriaChecklist criteria={blockerCriteria} />
        </div>
      )}

      {/* All-complete summary row */}
      {blockerIndex === -1 && (
        <div className="flex items-center justify-between gap-3 py-2">
          <span className="text-sm font-medium text-text-primary">
            All phases complete
          </span>
          <Badge variant="success">Complete</Badge>
        </div>
      )}
    </div>,
  );
}

export default PhaseGovernanceSection;
